import React, { useState, useEffect, useRef } from "react";
import { Autocomplete, TextField, Box, Chip } from "@mui/material";
import DnDPatientView from "../components/DnDPatientView";
import MonthlyCheckIn from "../components/MonthlyCheckIn";
import FacilityManagementView from "../facilitymanagement/FacilityManagementView";
import ConsentPatientView from "../consent/ConsentPatientView"

import { useAuth } from "../components/TokenHandler";
import dayjs from "dayjs";

const Dashboard = ({ selectedAction, setSelectedAction }) => {
    const [selectedOrganization, setSelectedOrganization] = useState(null);
    const [selectedFacility, setSelectedFacility] = useState(null);
    const [searchPatient, setSearchPatient] = useState(null);
    const [patientSearchInput, setPatientSearchInput] = useState("");
    const [organizations, setOrganizations] = useState([]);
    const [facilities, setFacilities] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [patients, setPatients] = useState([]);
    const [patientsLoading, setPatientsLoading] = useState(false);

    const [filteredPatients, setFilteredPatients] = useState([]);
    const [checkinsFetched, setCheckinsFetched] = useState(false);
    const [orgSearchTerm, setOrgSearchTerm] = useState("");
    const [organizationOptions, setOrganizationOptions] = useState([]);
    const latestQueryRef = useRef("");

    const isInactiveAccount = (status) =>
        String(status || "").trim().toLowerCase() === "inactive";

    const getAccountStatusLabel = (status) => {
        const clean = String(status || "").trim();
        return clean || "Unknown";
    };

    const getInactiveOptionStyles = (status, extra = {}) => {
        const inactive = isInactiveAccount(status);

        return {
            backgroundColor: inactive ? "#ffebee" : extra.backgroundColor || "white",
            borderLeft: inactive ? "5px solid #d32f2f" : extra.borderLeft,
            ...extra,
        };
    };

    const AccountStatusChip = ({ status }) => {
        const inactive = isInactiveAccount(status);

        return (
            <Chip
                size="small"
                label={getAccountStatusLabel(status)}
                sx={{
                    height: "20px",
                    fontSize: "11px",
                    fontWeight: 700,
                    backgroundColor: inactive ? "#d32f2f" : "#e8f5e9",
                    color: inactive ? "#fff" : "#2e7d32",
                }}
            />
        );
    };

    // ---------- Facility Management state ----------
    const [fm_facilities, setFmFacilities] = useState([]);              // rows[]
    const [fmLoading, setFmLoading] = useState(false);
    const [fmError, setFmError] = useState(null);
    const [fmSearchInput, setFmSearchInput] = useState("");

    // NEW: filters to be sent to combined_facility_list3
    const [fmServices, setFmServices] = useState([]);                   // ["RPM Passive", ...]
    const [fmOnDate, setFmOnDate] = useState("");                       // "YYYY-MM-DD"

    // NEW: pagination from API
    const [fmPagination, setFmPagination] = useState({
        total: 0,
        page: 1,
        pageSize: 50,
        totalPages: 0,
        hasPrev: false,
        hasNext: false,
        nextPage: null,
        prevPage: null,
    });
    //console.log(fmPagination)

    const combinedOptions = React.useMemo(() => {
        const corpMap = new Map();
        (fm_facilities || []).forEach((facility) => {
            if (!corpMap.has(facility.corp_name)) {
                corpMap.set(facility.corp_name, {
                    type: "corp",
                    corp_name: facility.corp_name,
                });
            }
        });
        const corpOptions = Array.from(corpMap.values());
        const facilityOptions = (fm_facilities || []).map((f) => ({
            type: "facility",
            ...f,
        }));
        return [...corpOptions, ...facilityOptions];
    }, [fm_facilities]);

    const {
        keycloak: kc,
        authenticated,
        infoMessage,
        setInfoMessage,
        givenName,
        email,
        roles,
    } = useAuth();

    // ---------- Fetch Facility Management data using new API ----------
    // ---------- Fetch Facility Management data using new API ----------
    const fetchFmFacilities = (() => {
        let inFlight;

        // helper: true if input looks like "297, 414903" (commas required)
        const isCommaIdList = (s) => {
            if (!s) return false;
            return s.includes(",");
        };

        // helper: normalize "297,  414903" -> "297,414903"
        // (backend supports spaces too, but keeping it tidy helps debugging)
        const normalizeIdListString = (s) =>
            String(s || "")
                .split(",")
                .map(x => x.trim())
                .filter(Boolean)
                .join(",");

        return async (opts = {}) => {
            const page = Math.max(1, Number(opts.page ?? fmPagination.page ?? 1) || 1);
            const count = Math.max(1, Number(opts.pageSize ?? fmPagination.pageSize ?? 50) || 50);

            if (inFlight) inFlight.abort();
            inFlight = new AbortController();

            setFmLoading(true);
            setFmError(null);

            try {
                const base = process.env.REACT_APP_API_URL;
                const params = new URLSearchParams();

                // paging
                params.set("page", String(page));
                params.set("pageSize", String(count));

                // ---- UPDATED: search vs facility_unique_id behavior ----
                const rawInput = (fmSearchInput || "").trim();

                if (rawInput.length >= 2) {
                    if (isCommaIdList(rawInput)) {
                        // if commas exist, treat as facility_unique_id list ONLY
                        params.set("facility_unique_id", normalizeIdListString(rawInput));
                        // IMPORTANT: do NOT set "search" in this mode
                    } else {
                        // normal behavior: use search
                        params.set("search", rawInput);
                    }
                }

                // on: must be YYYY-MM-DD
                if (fmOnDate && /^\d{4}-\d{2}-\d{2}$/.test(fmOnDate)) {
                    params.set("on", fmOnDate);
                }

                // services: comma-separated
                if (fmServices?.length) {
                    params.set("services", fmServices.join(","));
                }

                const url = `${base}/combined_facility_list8?${params.toString()}`;

                const resp = await fetch(url, {
                    method: "GET",
                    headers: {
                        Authorization: kc?.token ? `Bearer ${kc.token}` : undefined,
                        "Content-Type": "application/json",
                    },
                    signal: inFlight.signal,
                });

                if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                const data = await resp.json();

                setFmFacilities(Array.isArray(data?.rows) ? data.rows : []);
                const p = data?.pagination || {};

                setFmPagination({
                    total: p.total ?? 0,
                    page: p.page ?? page,
                    pageSize: count,
                    totalPages: p.totalPages ?? 0,
                    hasPrev: p.hasPrev ?? false,
                    hasNext: p.hasNext ?? false,
                    nextPage: p.nextPage ?? null,
                    prevPage: p.prevPage ?? null,
                });
            } catch (err) {
                if (err?.name !== "AbortError") {
                    console.error("FM fetch failed:", err);
                    setFmError("Failed to fetch facilities.");
                }
            } finally {
                setFmLoading(false);
            }
        };
    })();


    const reloadFacilities = () => {
        fetchFmFacilities({ page: 1 });
    };


    // react to action switch or search input changes
    useEffect(() => {
        if (selectedAction === "Facility Management") {
            // reset to first page when inputs change
            fetchFmFacilities({ page: 1 });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAction, fmSearchInput, fmServices, fmOnDate]);

    // ---------- Existing org + facility + other logic (unchanged except imports) ----------
    useEffect(() => {
        const controller = new AbortController();

        const fetchSearchResults = async () => {
            try {
                if (orgSearchTerm.length < 2) {
                    setOrganizationOptions([]);
                    return;
                }

                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/search_orgs4?q=${encodeURIComponent(orgSearchTerm)}`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${kc.token}`,
                            "Content-Type": "application/json",
                        },
                        signal: controller.signal,
                    }
                );

                if (!response.ok) {
                    console.error("Search API error:", response.statusText);
                    return;
                }

                const data = await response.json();


                if (orgSearchTerm === latestQueryRef.current) {
                    setOrganizationOptions(data);
                }
            } catch (err) {
                if (err.name !== "AbortError") {
                    console.error("Search fetch error:", err);
                }
            }
        };

        const delayDebounce = setTimeout(() => {
            latestQueryRef.current = orgSearchTerm;
            fetchSearchResults();
        }, 300);

        return () => {
            clearTimeout(delayDebounce);
            controller.abort();
        };
    }, [orgSearchTerm, kc?.token]);

    useEffect(() => {
        const fetchOrganizations = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/corporations`, {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${kc.token}`,
                        "Content-Type": "application/json",
                    },
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }

                const data = await response.json();
                const organizationNames = data.map((org, index) => ({
                    corp_name: org.corp_name,
                    corp_unique_id: org.corp_unique_id,
                    ouid: org.ouid,
                    key: `${org.corp_name}-${org.corp_unique_id}-${index}`,
                }));

                setOrganizations(organizationNames);
            } catch (error) {
                console.error("Error fetching organizations:", error);
            }
        };

        if (authenticated) fetchOrganizations();
    }, [authenticated, kc?.token]);

    const fetchFacilitiesAndBuildings = async () => {

        if (!selectedOrganization) return;
        try {
            const selectedOrg = organizationOptions.find(
                (org) => org.corp_name === selectedOrganization.corp_name,
            );
            if (!selectedOrg) return;

            const corpUniqueId = selectedOrg.corp_unique_id;

            const buildingResponse = await fetch(
                `${process.env.REACT_APP_API_URL}/building_mapping/${corpUniqueId}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${kc.token}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            if (!buildingResponse.ok) {
                throw new Error(`Failed to fetch buildings: ${buildingResponse.statusText}`);
            }
            const buildingsData = await buildingResponse.json();
            const buildingOptions = buildingsData.map((building) => ({
                building_unique_id: building.building_unique_id,
                corp_unique_id: corpUniqueId,
                legal_name: building.legal_name,
                address1: building.address1,
                city: building.city,
                state: building.state,
                zip_code: building.zip_code,
                phone: building.phone,
                timezone: building.timezone,
                account_status: building.account_status,
            }));
            setBuildings(buildingOptions);

            const facilityResponse = await fetch(
                `${process.env.REACT_APP_API_URL}/facility_list2/${corpUniqueId}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${kc.token}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            if (!facilityResponse.ok) {
                throw new Error(`Failed to fetch facilities: ${facilityResponse.statusText}`);
            }

            const facilitiesData = await facilityResponse.json();
            const facilityOptions = facilitiesData.map((facility, index) => ({
                facility_name: facility.facility_name,
                facility_unique_id: facility.facility_unique_id,
                fac_id: facility.fac_id,
                ouid: facility.ouid,
                ehr_ds: facility.ehr_ds ?? null,
                corp_unique_id: corpUniqueId,
                address1: facility.address1,
                city: facility.city,
                state: facility.state,
                zip_code: facility.zip_code,
                timezone: facility.timezone,
                provider_number: facility.source_id,
                account_status: facility.account_status,
                key: `${facility.facility_name}-${facility.fac_id}-${index}`,
            }));
            setFacilities(facilityOptions);



            const matchedFacility = facilityOptions.find(
                (facility) => facility.facility_unique_id === selectedOrganization.facility_unique_id
            );

            if (matchedFacility) {
                setSelectedFacility({
                    ...matchedFacility,
                    monday_facility_name:
                        selectedOrganization.monday_facility_name ??
                        matchedFacility.monday_facility_name ??
                        null,
                    ehr_ds:
                        selectedOrganization.ehr_ds ??
                        matchedFacility.ehr_ds ??
                        null,
                    source_system:
                        selectedOrganization.source_system ??
                        matchedFacility.source_system ??
                        null,
                });
            }
            try {
                const requestBody = buildingOptions.map((building) => ({
                    building_unique_id: building.building_unique_id,
                    corp_unique_id: corpUniqueId,
                }));
                const checkinResponse = await fetch(
                    `${process.env.REACT_APP_API_URL}/get_checkin_latest2`,
                    {
                        method: "POST",
                        headers: {
                            Authorization: `Bearer ${kc.token}`,
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(requestBody),
                    }
                );
                if (checkinResponse.ok) {
                    const checkinData = await checkinResponse.json();
                    const updatedBuildings = buildingOptions.map((building) => {
                        const matchingCheckin = checkinData.find(
                            (checkin) =>
                                checkin.building_unique_id === building.building_unique_id &&
                                checkin.corp_unique_id === corpUniqueId
                        );
                        return {
                            ...building,
                            lastcheckin: matchingCheckin ? matchingCheckin.lastcheckin : "unknown",
                        };
                    });
                    setBuildings(updatedBuildings);

                } else {
                    console.error("Failed to fetch latest check-ins:", checkinResponse.statusText);
                }
            } catch (error) {
                console.error("Error fetching latest check-ins:", error);
            }
        } catch (error) {
            console.error("Error fetching facilities and buildings:", error);
        }
    };

    useEffect(() => {
        fetchFacilitiesAndBuildings();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOrganization, organizations]);


    useEffect(() => {
        const shouldLoadPatients =
            !!selectedFacility?.fac_id &&
            !!selectedFacility?.ouid &&
            (selectedAction === "Opt out" || selectedAction === "Consent");

        if (!shouldLoadPatients) {
            setPatients([]);
            setFilteredPatients([]);
            setSearchPatient(null);
            setPatientSearchInput("");
            setPatientsLoading(false);
            return;
        }

        const controller = new AbortController();

        const fetchPatients = async () => {
            setPatientsLoading(true);

            try {
                const params = new URLSearchParams();
                params.set("fac_id", String(selectedFacility.fac_id));
                params.set("ouid", String(selectedFacility.ouid));

                if (selectedFacility.ehr_ds != null && String(selectedFacility.ehr_ds).trim() !== "") {
                    params.set("ehr_ds", String(selectedFacility.ehr_ds).trim());
                }

                const url = `${process.env.REACT_APP_API_URL}/patients2?${params.toString()}`;

                const response = await fetch(url, {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${kc.token}`,
                        "Content-Type": "application/json",
                    },
                    signal: controller.signal,
                });

                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const data = await response.json();

                setPatients(Array.isArray(data) ? data : []);
                setFilteredPatients(Array.isArray(data) ? data : []);
            } catch (error) {
                if (error?.name !== "AbortError") {
                    console.error("Error fetching patients:", error);
                    setPatients([]);
                    setFilteredPatients([]);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setPatientsLoading(false);
                }
            }
        };

        fetchPatients();

        return () => controller.abort();
    }, [
        selectedFacility?.fac_id,
        selectedFacility?.ouid,
        selectedFacility?.ehr_ds,
        selectedAction,
        kc?.token,
    ]);

    const hasRole = (role) => roles?.includes(role);

    const actionOptions = React.useMemo(() => {
        if (hasRole("admin_role")) {
            return ["Consent", "Monthly check in", "Opt out", "Facility Management"];
        }

        return [
            ...((hasRole("consent_role") || hasRole("consent_role_read_only")) ? ["Consent"] : []),
            ...(hasRole("monthly_checkin_role") ? ["Monthly check in"] : []),
            ...(hasRole("do_not_do_role") ? ["Opt out"] : []),
            ...(hasRole("facility_management_role") ? ["Facility Management"] : []),
        ];
    }, [roles]);

    useEffect(() => {
        if (!selectedAction || !actionOptions.includes(selectedAction)) {
            setSelectedAction(actionOptions[0] || null);
        }
    }, [actionOptions, selectedAction, setSelectedAction]);

    const handleSelectChange = (event, value) => {
        const selectedOrg = organizations.find((org) => org.corp_name === value);
        setSelectedOrganization(selectedOrg);
        setSelectedFacility(null);
    };

    const handleFacilityChange = (event, value) => {
        if (!value) {
            setSelectedFacility(null);
            return;
        }

        // If this facility carries ehr_ds, preserve the full object exactly as selected
        if (value.ehr_ds) {
           // console.log("Setting selectedFacility from full selected value (Matrix Care SF):", value);
            setSelectedFacility(value);
            return;
        }

        // Otherwise, resolve from the loaded facilities list
        const matchedFacility = facilities.find(
            (facility) =>
                facility.facility_unique_id === value.facility_unique_id
        );

      //  console.log("Setting selectedFacility from matched facility list row:", matchedFacility || value);
        setSelectedFacility(matchedFacility || value);
    };

    const handleActionChange = (event, value) => {
        setSelectedAction(value);
        if (value !== "Monthly check in") {
            setSelectedFacility(null);
        }
    };

    const normalizeSearchText = (value) =>
        String(value || "")
            .toLowerCase()
            .trim()
            .replace(/\s+/g, " ");

    const getPatientSearchText = (patient) =>
        normalizeSearchText([
            patient.first_name,
            patient.last_name,
            `${patient.first_name || ""} ${patient.last_name || ""}`,
            patient.mrn_id,
            patient.patient_id,
            patient.patient_unique_id,
        ].filter(Boolean).join(" "));

    const filterPatientsBySearch = (rawValue) => {
        const searchQuery = normalizeSearchText(rawValue);

        setPatientSearchInput(rawValue || "");

        if (!searchQuery) {
            setFilteredPatients(patients);
            return;
        }

        const searchParts = searchQuery.split(" ").filter(Boolean);

        const filtered = patients.filter((patient) => {
            const patientSearchText = getPatientSearchText(patient);

            return searchParts.every((part) =>
                patientSearchText.includes(part)
            );
        });

        setFilteredPatients(filtered);
    };

    const handlePatientInputChange = (event, newInputValue, reason) => {
        if (reason === "reset") {
            return;
        }

        if (reason === "blur") {
            return;
        }

        filterPatientsBySearch(newInputValue);
    };

    const handlePatientChange = (event, selectedPatient) => {
        if (typeof selectedPatient === "string") {
            setSearchPatient(null);
            filterPatientsBySearch(selectedPatient);
            return;
        }

        setSearchPatient(selectedPatient);

        if (!selectedPatient) {
            setPatientSearchInput("");
            setFilteredPatients(patients);
            return;
        }

        const selectedName = `${selectedPatient.first_name || ""} ${selectedPatient.last_name || ""}`.trim();

        setPatientSearchInput(selectedName);
        setFilteredPatients([selectedPatient]);
    };

    const getFacilityDisplayName = (option) => {
        const facilityName = String(option?.facility_name || "").trim();
        const mondayFacilityName = String(option?.monday_facility_name || "").trim();

        if (facilityName && mondayFacilityName && facilityName !== mondayFacilityName) {
            return `${mondayFacilityName} / ${facilityName}`;
        }

        return mondayFacilityName || facilityName || "";
    };

    const getOrgSearchOptionText = (option) =>
        [
            option?.corp_name,
            option?.facility_name,
            option?.monday_facility_name,
            option?.source_id,
            option?.state,
            option?.city,
        ]
            .filter(Boolean)
            .join(" ");

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 4, padding: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box sx={{ display: "flex", gap: 2 }}>
                    {selectedAction === "Facility Management" ? (
                        <Box sx={{ width: "350px" }}>
                            <Autocomplete
                                freeSolo
                                options={combinedOptions.filter((option) => {
                                    const lower = fmSearchInput.toLowerCase();
                                    if (option.type === "corp") {
                                        return option.corp_name.toLowerCase().includes(lower);
                                    } else {
                                        return (
                                            `${option.corp_name} ${option.facility_name} ${option.source_id}`.toLowerCase().includes(lower)
                                        );
                                    }
                                })}
                                inputValue={fmSearchInput}
                                onInputChange={(_, newInputValue) => setFmSearchInput(newInputValue)}
                                onChange={(_, selectedOption) => {
                                    if (!selectedOption) {
                                        setFmSearchInput("");
                                        fetchFmFacilities({ page: 1 });
                                        return;
                                    }
                                    if (selectedOption.type === "corp") {
                                        setFmSearchInput(selectedOption.corp_name);
                                    } else {
                                        setFmSearchInput(`${selectedOption.facility_name}`);
                                    }
                                }}
                                getOptionLabel={(option) => {
                                    if (option.type === "corp") return option.corp_name;
                                    return `${option.corp_name} - ${option.facility_name} (${option.source_id || "N/A"})`;
                                }}
                                renderOption={(props, option, { index }) => {
                                    const isCorp = option.type === "corp";
                                    return (
                                        <li
                                            {...props}
                                            style={{
                                                backgroundColor: isCorp ? "#f1f1f1" : "white",
                                                padding: "8px",
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "flex-start",
                                                textAlign: "left",
                                            }}
                                        >
                                            {isCorp ? (
                                                <Box sx={{ fontWeight: "bold", textAlign: "left", width: "100%" }}>
                                                    {option.corp_name}
                                                </Box>
                                            ) : (
                                                <>
                                                    <Box sx={{ fontWeight: 500, textAlign: "left", width: "100%" }}>
                                                        {option.facility_name}
                                                        {option.monday_facility_name && option.monday_facility_name !== option.facility_name
                                                            ? ` / Monday: ${option.monday_facility_name}`
                                                            : ""}
                                                    </Box>
                                                    <Box sx={{ fontSize: "12px", color: "#666", textAlign: "left", width: "100%" }}>
                                                        {option.corp_name} | {option.source_id || "N/A"}
                                                    </Box>
                                                </>
                                            )}
                                        </li>
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Search Facilities or Corporations"
                                        variant="standard"
                                        sx={{
                                            backgroundColor: "#F9F9F9",
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset": { borderColor: "#CCCCCC" },
                                                "&:hover fieldset": { borderColor: "#F9F9F9" },
                                                "&.Mui-focused fieldset": { borderColor: "#F9F9F9" },
                                            },
                                            "& .MuiInputLabel-root": { color: "#666666" },
                                            "& .MuiInputLabel-shrink": { color: "#666666" },
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    ) : (
                        <Box sx={{ width: "250px" }}>
                            <Autocomplete
                                options={organizationOptions}
                                value={selectedOrganization}
                                onInputChange={(_, newInputValue) => setOrgSearchTerm(newInputValue)}
                                onChange={(event, value) => {
                                    setSelectedOrganization(value);
                                    setSelectedFacility(null);
                                }}
                                getOptionLabel={(option) =>
                                    typeof option === "string"
                                        ? option
                                        : `${option.corp_name} ${getFacilityDisplayName(option)} ${option.source_id || ""}`
                                }
                                isOptionEqualToValue={(option, value) =>
                                    option?.corp_unique_id === value?.corp_unique_id &&
                                    option?.facility_unique_id === value?.facility_unique_id
                                }
                                renderOption={(props, option) => {
                                    const inactive = isInactiveAccount(option.account_status);

                                    return (
                                        <li
                                            {...props}
                                            style={getInactiveOptionStyles(option.account_status, {
                                                display: "flex",
                                                flexDirection: "column",
                                                fontSize: "14px",
                                                padding: "8px",
                                                textAlign: "left",
                                                width: "100%",
                                                alignItems: "flex-start",
                                            })}
                                        >
                                            <Box
                                                sx={{
                                                    width: "100%",
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    gap: 1,
                                                    alignItems: "center",
                                                }}
                                            >
                <span
                    style={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: inactive ? "#b71c1c" : "inherit",
                    }}
                >
                    {option.corp_name}
                </span>

                                                <AccountStatusChip status={option.account_status} />
                                            </Box>

                                            <span
                                                style={{
                                                    fontSize: "12px",
                                                    color: inactive ? "#b71c1c" : "#666",
                                                    fontWeight: inactive ? 700 : 400,
                                                }}
                                            >
                {option.monday_facility_name && (
                    <>
                        Monday: {option.monday_facility_name}
                        <br />
                    </>
                )}

                                                Facility: {option.facility_name || "N/A"} | {option.source_id || "N/A"}
                                                {option.state ? ` | ${option.state}` : ""}
            </span>
                                        </li>
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Search by Facility, Corp or Provider#"
                                        variant="standard"
                                        sx={{
                                            backgroundColor: "#F9F9F9",
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset": { borderColor: "#CCCCCC" },
                                                "&:hover fieldset": { borderColor: "#F9F9F9" },
                                                "&.Mui-focused fieldset": { borderColor: "#F9F9F9" },
                                            },
                                            "& .MuiInputLabel-root": { color: "#666666" },
                                            "& .MuiInputLabel-shrink": { color: "#666666" },
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    )}

                    {(selectedAction === "Consent" || selectedAction === "Opt out") && (
                        <Box sx={{ width: "450px" }}>
                            <Autocomplete
                                options={facilities}
                                value={selectedFacility}
                                onChange={handleFacilityChange}
                                getOptionLabel={(option) => (option ? option.facility_name : "")}
                                isOptionEqualToValue={(option, value) =>
                                    option?.facility_unique_id === value?.facility_unique_id
                                }
                                renderOption={(props, option) => {
                                    const inactive = isInactiveAccount(option.account_status);

                                    return (
                                        <li
                                            {...props}
                                            style={getInactiveOptionStyles(option.account_status, {
                                                display: "flex",
                                                flexDirection: "column",
                                                fontSize: "14px",
                                                padding: "8px",
                                            })}
                                        >
                                            <Box
                                                sx={{
                                                    width: "100%",
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    gap: 1,
                                                    alignItems: "center",
                                                }}
                                            >
                <span
                    style={{
                        fontSize: "14px",
                        alignSelf: "flex-start",
                        color: inactive ? "#b71c1c" : "inherit",
                        fontWeight: inactive ? 700 : 400,
                    }}
                >
                    {option.facility_name}
                </span>

                                                <AccountStatusChip status={option.account_status} />
                                            </Box>

                                            <span
                                                style={{
                                                    fontSize: "12px",
                                                    color: inactive ? "#b71c1c" : "#666",
                                                    fontWeight: "bold",
                                                    alignSelf: "flex-start",
                                                }}
                                            >
                ({option.provider_number ? option.provider_number : "N/A"} - {option.state})
            </span>
                                        </li>
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select a Facility"
                                        variant="standard"
                                        disabled={selectedAction === "Monthly check in"}
                                        sx={{
                                            backgroundColor: "#F9F9F9",
                                            width: selectedFacility
                                                ? `calc(${selectedFacility.facility_name.length}ch + 50px)`
                                                : "250px",
                                            minWidth: "250px",
                                            maxWidth: "600px",
                                            height: "40px",
                                            display: "flex",
                                            alignItems: "center",
                                            "& .MuiInputBase-root": {
                                                height: "40px",
                                                display: "flex",
                                                alignItems: "center",
                                            },
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset": { borderColor: "#CCCCCC" },
                                                "&:hover fieldset": { borderColor: "#F9F9F9" },
                                                "&.Mui-focused fieldset": { borderColor: "#F9F9F9" },
                                            },
                                            "& .MuiInputLabel-root": { color: "#666666" },
                                            "& .MuiInputLabel-shrink": { color: "#666666" },
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    )}
                </Box>

                <Box sx={{ display: "flex", gap: 2 }}>
                    <Box sx={{ width: "250px" }}>
                        <Autocomplete
                            options={actionOptions}
                            value={selectedAction}
                            onChange={handleActionChange}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Action"
                                    variant="standard"
                                    sx={{
                                        backgroundColor: "#F9F9F9",
                                        "& .MuiOutlinedInput-root": {
                                            "& fieldset": { borderColor: "#CCCCCC" },
                                            "&:hover fieldset": { borderColor: "#F9F9F9" },
                                            "&.Mui-focused fieldset": { borderColor: "#F9F9F9" },
                                        },
                                        "& .MuiInputLabel-root": { color: "#666666" },
                                        "& .MuiInputLabel-shrink": { color: "#666666" },
                                    }}
                                />
                            )}
                        />
                    </Box>

                    {(selectedAction === "Consent" || selectedAction === "Opt out") && (
                        <Box sx={{ width: "250px" }}>
                            <Autocomplete
                                freeSolo
                                clearOnBlur={false}
                                autoSelect={false}
                                options={filteredPatients}
                                value={searchPatient}
                                inputValue={patientSearchInput}
                                onInputChange={handlePatientInputChange}
                                onChange={handlePatientChange}
                                getOptionLabel={(option) => {
                                    if (!option) return "";
                                    if (typeof option === "string") return option;

                                    return `${option.first_name || ""} ${option.last_name || ""}`.trim();
                                }}
                                isOptionEqualToValue={(option, value) =>
                                    option?.patient_id === value?.patient_id
                                }
                                renderOption={(props, option) => (
                                    <li {...props}>
                                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                                            <Box sx={{ fontWeight: 500 }}>
                                                {option.first_name} {option.last_name}
                                            </Box>

                                            <Box sx={{ fontSize: "12px", color: "#666" }}>
                                                MRN: {option.mrn_id || "N/A"}
                                                {option.patient_id ? ` | Patient ID: ${option.patient_id}` : ""}
                                            </Box>
                                        </Box>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Search Patient"
                                        variant="standard"
                                        disabled={!(selectedAction === "Opt out" || selectedAction === "Consent")}
                                        sx={{
                                            backgroundColor: "#F9F9F9",
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset": { borderColor: "#CCCCCC" },
                                                "&:hover fieldset": { borderColor: "#F9F9F9" },
                                                "&.Mui-focused fieldset": { borderColor: "#F9F9F9" },
                                            },
                                            "& .MuiInputLabel-root": { color: "#666666" },
                                            "& .MuiInputLabel-shrink": { color: "#666666" },
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    )}
                </Box>
            </Box>

            {selectedAction === "Opt out" && (
                <DnDPatientView
                    patients={patients}
                    filteredPatients={filteredPatients}
                    facility_unique_id={selectedFacility ? selectedFacility.facility_unique_id : null}
                    facility_name={selectedFacility ? selectedFacility.facility_name : null}
                    fac_id={selectedFacility ? selectedFacility.fac_id : null}
                    provider_number={selectedFacility ? selectedFacility.provider_number : null}
                    ouid={selectedFacility ? selectedFacility.ouid : null}
                />
            )}

            {selectedAction === "Consent" && (
                <ConsentPatientView
                    patients={patients}
                    filteredPatients={filteredPatients}
                    patientsLoading={patientsLoading}
                    facility_unique_id={selectedFacility ? selectedFacility.facility_unique_id : null}
                    facility_name={selectedFacility ? selectedFacility.facility_name : null}
                    fac_id={selectedFacility ? selectedFacility.fac_id : null}
                    provider_number={selectedFacility ? selectedFacility.provider_number : null}
                    ouid={selectedFacility ? selectedFacility.ouid : null}
                    ehr_ds={selectedFacility ? selectedFacility.ehr_ds : null}
                />
            )}


            {selectedAction === "Monthly check in" && (
                <MonthlyCheckIn buildings={buildings} fetchFacilitiesAndBuildings={fetchFacilitiesAndBuildings} />
            )}

            {selectedAction === "Facility Management" && (
                <FacilityManagementView
                    facilities={fm_facilities}
                    loading={fmLoading}
                    error={fmError}
                    onApply={() => fetchFmFacilities({ page: 1 })}
                    reloadFacilities={() => fetchFmFacilities({ page: 1 })}

                    services={fmServices}
                    onChangeServices={(arr) => setFmServices(arr || [])}
                    date={fmOnDate}
                    onChangeDate={(str) => setFmOnDate(str || "")}

                    pagination={fmPagination}
                    onPageChange={(p) => fetchFmFacilities({ page: p })}
                    onPageSizeChange={(n) => {
                        // update local pagination state immediately
                        setFmPagination((prev) => ({
                            ...prev,
                            page: 1,
                            pageSize: n,
                        }));

                        // then refetch with the new page size
                        fetchFmFacilities({ page: 1, pageSize: n });
                    }}

                />

            )}
        </Box>
    );
};

export default Dashboard;
